import { useCallback } from 'react';
import { GROUP_BY_STATUS } from '@atlassian/jira-business-constants';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useFlagsService, toFlagId } from '@atlassian/jira-flags';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useBoardStore } from '../../services/board-store';
import { setColumnsOld as persistStatusColumnsOld } from '../../utils/columns-order';
import { useColumns } from '../columns-old';
import { useColumnsOrder } from '../columns-order-old';
import { useGroupBy } from '../group-by';
import messages from './messages';
import type { ColumnMoveHandlerReturnTypeOld } from './types';

export const MOVE_COLUMN_ERROR_FLAG_ID = toFlagId('move-column-error-flag');

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useColumnMoveHandlerOld = (): ColumnMoveHandlerReturnTypeOld => {
	const [{ workflowName }, { setColumns }] = useBoardStore();
	const boardColumns = useColumns();
	const [groupBy] = useGroupBy();
	const [, setColumnsOrder] = useColumnsOrder();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();

	const project = useProject();
	const projectId = String(project.id);

	const onColumnMove = useCallback(
		async (columnId: number, sourceIndex: number, destinationIndex: number) => {
			if (sourceIndex === destinationIndex) {
				return;
			}

			if (workflowName != null) {
				fireUIAnalytics(createAnalyticsEvent({}), 'column dragged', 'board');

				const updatedColumns = [...boardColumns];
				const [sourceColumn] = updatedColumns.splice(sourceIndex, 1);
				updatedColumns.splice(destinationIndex, 0, sourceColumn);

				if (groupBy == null || groupBy === GROUP_BY_STATUS) {
					// the columns order when grouping by status
					// is stored in the backend so we not persist that in our preferences
					// @ts-expect-error  - TS2345 - Argument of type BoardColumn[] is not assignable to parameter of type Column[] (TODO fix with BAE-23)
					setColumns(updatedColumns);

					try {
						// @ts-expect-error  - TS2345 - Argument of type BoardColumn[] is not assignable to parameter of type Column[] (TODO fix with BAE-23)
						await persistStatusColumnsOld(projectId, workflowName, updatedColumns);
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
					} catch (error: any) {
						// @ts-expect-error  - TS2345 - Argument of type BoardColumn[] is not assignable to parameter of type Column[] (TODO fix with BAE-23)
						setColumns(boardColumns);
						fireErrorAnalytics({
							meta: {
								id: 'jwmBoardControllerOnColumnMove',
								packageName: 'jiraWorkManagementBoard',
								teamName: 'wanjel',
							},
							attributes: {
								message: 'Failed to set column order in JWM board',
							},
							error,
							sendToPrivacyUnsafeSplunk: true,
						});
						showFlag({
							id: MOVE_COLUMN_ERROR_FLAG_ID,
							type: 'error',
							title: messages.columnMoveErrorTitle,
							description: messages.columnMoveErrorMessage,
						});
					}
				} else {
					setColumnsOrder(updatedColumns);
				}
			}
		},
		[
			projectId,
			workflowName,
			boardColumns,
			createAnalyticsEvent,
			groupBy,
			setColumns,
			setColumnsOrder,
			showFlag,
		],
	);

	return { onColumnMove };
};
